/**
 * @Author: Klaudie <klaudie@foxprofi.cz>
 */

import React from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { ArrowLeft, Trash2 } from 'lucide-react'
import { getUser, updateUser, deleteUser, type UpdateUserRequest } from '@/api/admin/users.api'
import { getRoles } from '@/api/admin/roles.api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

export function UserEditPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const { data: user, isLoading: userLoading } = useQuery({
    queryKey: ['user', id],
    queryFn: () => getUser(id!),
    enabled: !!id,
  })

  const { data: roles, isLoading: rolesLoading } = useQuery({
    queryKey: ['roles'],
    queryFn: getRoles,
  })

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    reset,
  } = useForm<UpdateUserRequest>()

  // Reset form when user data loads
  React.useEffect(() => {
    if (user) {
      reset({
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
        phone: user.phone || '',
        avatar: user.avatar || '',
        isActive: user.isActive,
        roleIds: user.roles,
      })
    }
  }, [user, reset])

  const updateMutation = useMutation({
    mutationFn: (data: UpdateUserRequest) => updateUser(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      queryClient.invalidateQueries({ queryKey: ['user', id] })
      toast.success('Uživatel byl úspěšně aktualizován')
      navigate('/admin/users')
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Nepodařilo se aktualizovat uživatele'
      toast.error(message)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: () => deleteUser(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      toast.success('Uživatel byl úspěšně smazán')
      navigate('/admin/users')
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Nepodařilo se smazat uživatele'
      toast.error(message)
    },
  })

  const onSubmit = (data: UpdateUserRequest) => {
    updateMutation.mutate(data)
  }

  const handleDelete = () => {
    if (confirm('Opravdu chcete smazat tohoto uživatele?')) {
      deleteMutation.mutate()
    }
  }

  const selectedRoles = watch('roleIds') || []

  const toggleRole = (roleId: string) => {
    const current = selectedRoles
    if (current.includes(roleId)) {
      setValue('roleIds', current.filter((id) => id !== roleId))
    } else {
      setValue('roleIds', [...current, roleId])
    }
  }

  if (userLoading) {
    return (
      <div className="p-6">
        <p>Načítání...</p>
      </div>
    )
  }

  if (!user) {
    return (
      <div className="p-6">
        <p className="text-destructive">Uživatel nenalezen</p>
      </div>
    )
  }

  return (
    <div className="p-6 max-w-2xl">
      {/* Header */}
      <div className="mb-6">
        <Button variant="ghost" size="sm" onClick={() => navigate('/admin/users')} className="mb-4">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Zpět na seznam
        </Button>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Upravit uživatele</h1>
            <p className="text-muted-foreground mt-1">{user.email}</p>
          </div>
          <Button variant="destructive" size="sm" onClick={handleDelete} disabled={deleteMutation.isPending}>
            <Trash2 className="mr-2 h-4 w-4" />
            Smazat
          </Button>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Email */}
        <div className="space-y-2">
          <Label htmlFor="email">Email *</Label>
          <Input
            id="email"
            type="email"
            {...register('email', {
              required: 'Email je povinný',
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: 'Neplatný formát emailu',
              },
            })}
          />
          {errors.email && <p className="text-sm text-destructive">{errors.email.message}</p>}
        </div>

        {/* Password (optional for edit) */}
        <div className="space-y-2">
          <Label htmlFor="password">Heslo (ponechte prázdné pro zachování)</Label>
          <Input
            id="password"
            type="password"
            {...register('password', {
              minLength: {
                value: 8,
                message: 'Heslo musí mít alespoň 8 znaků',
              },
            })}
          />
          {errors.password && <p className="text-sm text-destructive">{errors.password.message}</p>}
        </div>

        {/* First Name */}
        <div className="space-y-2">
          <Label htmlFor="firstName">Jméno *</Label>
          <Input
            id="firstName"
            {...register('firstName', { required: 'Jméno je povinné' })}
          />
          {errors.firstName && <p className="text-sm text-destructive">{errors.firstName.message}</p>}
        </div>

        {/* Last Name */}
        <div className="space-y-2">
          <Label htmlFor="lastName">Příjmení *</Label>
          <Input
            id="lastName"
            {...register('lastName', { required: 'Příjmení je povinné' })}
          />
          {errors.lastName && <p className="text-sm text-destructive">{errors.lastName.message}</p>}
        </div>

        {/* Phone */}
        <div className="space-y-2">
          <Label htmlFor="phone">Telefon</Label>
          <Input id="phone" type="tel" {...register('phone')} />
        </div>

        {/* Avatar URL */}
        <div className="space-y-2">
          <Label htmlFor="avatar">Avatar URL</Label>
          <Input id="avatar" type="url" {...register('avatar')} />
        </div>

        {/* Is Active */}
        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            id="isActive"
            checked={watch('isActive')}
            onChange={(e) => setValue('isActive', e.target.checked)}
            className="h-4 w-4 rounded border-gray-300"
          />
          <Label htmlFor="isActive" className="cursor-pointer">
            Aktivní uživatel
          </Label>
        </div>

        {/* Roles */}
        <div className="space-y-2">
          <Label>Role *</Label>
          {rolesLoading ? (
            <p className="text-sm text-muted-foreground">Načítání rolí...</p>
          ) : (
            <div className="space-y-2 border rounded-md p-4">
              {roles?.map((role) => (
                <div key={role.id} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id={`role-${role.id}`}
                    checked={selectedRoles.includes(role.id)}
                    onChange={() => toggleRole(role.id)}
                    className="h-4 w-4 rounded border-gray-300"
                  />
                  <Label htmlFor={`role-${role.id}`} className="cursor-pointer">
                    {role.name}
                    {role.description && (
                      <span className="text-sm text-muted-foreground ml-2">
                        ({role.description})
                      </span>
                    )}
                  </Label>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-4">
          <Button type="submit" disabled={updateMutation.isPending}>
            {updateMutation.isPending ? 'Ukládání...' : 'Uložit změny'}
          </Button>
          <Button type="button" variant="outline" onClick={() => navigate('/admin/users')}>
            Zrušit
          </Button>
        </div>
      </form>
    </div>
  )
}
